import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, Search, CheckCircle2, AlertTriangle, MinusCircle, Download, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, Calendar } from 'lucide-react';
import { supabase } from '@/lib/supabaseClient';
import { toast } from 'sonner';
import { companyService } from '@/services/companyService';
import { useLanguage } from '@/contexts/LanguageContext';
import { ROUTES } from '@/lib/routes';
import type { CategoryKey } from '@/lib/navigation';
import { parseExpiryDate, areAllDatesSame } from '@/lib/skuUtils';
import * as XLSX from 'xlsx';
import { triggerReloadWithExpirySearchFilters, pickReloadSearch } from '@/utils/reloadSearch';
import { ensureAuthReady } from '@/lib/ensureAuthReady';
import { decodeHtmlEntities } from '@/lib/utils';

const EXPIRY_CATEGORY: CategoryKey = 'expiry';

// 核對結果類型
type VerificationStatus = 'MATCHED' | 'MISMATCHED' | 'NO_DATA';

// 產品核對結果介面
interface ProductVerification {
  sku_id: string;
  company_sku_key: string;
  barcode: string | null;
  brand: string;
  sku_name: string;
  sku_name_chi: string;
  sku_name_sc: string;
  sku_status: 'ONLINE' | 'OFFLINE';
  // 解析出的日期
  date_from_name: Date | null;
  date_from_chi: Date | null;
  date_from_sc: Date | null;
  // 核對結果
  verification_status: VerificationStatus;
}

// 分批取數輔助函式（避免 Supabase 單次 1000 筆限制）
const fetchAllData = async <T,>(
  query: any,
  batchSize: number = 1000
): Promise<T[]> => {
  let all: T[] = [];
  let start = 0;
  let hasMore = true;

  while (hasMore) {
    const { data, error } = await query.range(start, start + batchSize - 1);
    if (error) throw error;

    if (data && data.length > 0) {
      all = all.concat(data as T[]);
      start += batchSize;
      hasMore = data.length === batchSize;
    } else {
      hasMore = false;
    }
  }

  return all;
};

const ProductExpiryProcessing = () => {
  const [products, setProducts] = useState<ProductVerification[]>([]);
  const [filteredProducts, setFilteredProducts] = useState<ProductVerification[]>([]);
  const [loading, setLoading] = useState(false);
  const [currentCompany, setCurrentCompany] = useState<{ id: string; code?: string; name?: string } | null>(null);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  // 篩選條件
  const [searchSkuId, setSearchSkuId] = useState('');
  const [searchBarcode, setSearchBarcode] = useState('');
  const [searchProductName, setSearchProductName] = useState('');
  const [searchBrand, setSearchBrand] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('online');
  const [filterVerification, setFilterVerification] = useState<string>('MISMATCHED');

  const navigate = useNavigate();
  const { language } = useLanguage();

  // 獲取當前語言的產品名稱
  const getProductDisplayName = (product: ProductVerification): string => {
    let name: string;
    if (language === 'zh') {
      name = product.sku_name_chi || product.sku_name || product.sku_name_sc || '未命名產品';
    } else {
      name = product.sku_name || product.sku_name_chi || product.sku_name_sc || 'Unnamed Product';
    }
    return decodeHtmlEntities(name);
  };

  // 獲取核對結果的徽章
  const getVerificationBadge = (status: VerificationStatus) => {
    switch (status) {
      case 'MATCHED':
        return (
          <Badge variant="outline" className="text-xs border-green-500 bg-green-600 text-white">
            <CheckCircle2 className="w-3 h-3 mr-1" />
            {language === 'zh' ? '日期相同' : 'All Matched'}
          </Badge>
        );
      case 'MISMATCHED':
        return (
          <Badge variant="outline" className="text-xs border-yellow-500 bg-yellow-600 text-white">
            <AlertTriangle className="w-3 h-3 mr-1" />
            {language === 'zh' ? '不相符' : 'Mismatched'}
          </Badge>
        );
      case 'NO_DATA':
        return (
          <Badge variant="secondary" className="text-xs bg-gray-500 text-white border-gray-500">
            <MinusCircle className="w-3 h-3 mr-1" />
            {language === 'zh' ? '未有日期' : 'No Data'}
          </Badge>
        );
    }
  };

  // 獲取狀態徽章
  const getStatusBadge = (status: 'ONLINE' | 'OFFLINE') => {
    if (status === 'ONLINE') {
      return (
        <Badge variant="outline" className="text-xs border-green-500 bg-green-600 text-white">
          ONLINE
        </Badge>
      );
    } else {
      return (
        <Badge variant="secondary" className="text-xs bg-gray-500 text-white border-gray-500">
          OFFLINE
        </Badge>
      );
    }
  };

  // 格式化日期為 YYYY-MM-DD
  const formatDate = (date: Date | null): string => {
    if (!date) return '—';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // 核對產品的三個名稱欄位
  const verifyProduct = (product: any): ProductVerification => {
    // 解析三個欄位的日期
    const dateFromName = parseExpiryDate(product.sku_name || '');
    const dateFromChi = parseExpiryDate(product.sku_name_chi || '');
    const dateFromSc = parseExpiryDate(product.sku_name_sc || '');

    // 判斷核對結果
    let verificationStatus: VerificationStatus;
    
    if (dateFromName === null && dateFromChi === null && dateFromSc === null) {
      // 三個都沒有日期 → 沒資料
      verificationStatus = 'NO_DATA';
    } else if (dateFromName && dateFromChi && dateFromSc) {
      // 三個都有日期，檢查是否相同
      if (areAllDatesSame(dateFromName, dateFromChi, dateFromSc)) {
        verificationStatus = 'MATCHED';
      } else {
        verificationStatus = 'MISMATCHED';
      }
    } else {
      // 部分有日期，部分沒有 → 不相符
      verificationStatus = 'MISMATCHED';
    }

    return {
      sku_id: product.sku_id,
      company_sku_key: product.company_sku_key || product.sku_id,
      barcode: product.barcode,
      brand: product.brand || (language === 'zh' ? '未分類' : 'Uncategorized'),
      sku_name: product.sku_name || '',
      sku_name_chi: product.sku_name_chi || '',
      sku_name_sc: product.sku_name_sc || '',
      sku_status: (product.sku_status || '').toUpperCase() === 'ONLINE' ? 'ONLINE' : 'OFFLINE',
      date_from_name: dateFromName,
      date_from_chi: dateFromChi,
      date_from_sc: dateFromSc,
      verification_status: verificationStatus,
    };
  };

  // 加載數據
  const loadProducts = async (companyId: string | number) => {
    setLoading(true);
    try {
      const companyFilter = Number.isFinite(Number(companyId)) ? Number(companyId) : String(companyId);

      // Limited expiry verification page product loading to company-filtered queries only, avoiding cross-company data mixing

      // 僅載入當前公司的產品數據（分批取數，避免 1000 筆上限）
      const productsQuery = supabase
        .from('products')
        .select(`
          sku_id,
          company_sku_key,
          barcode,
          brand,
          sku_name,
          sku_name_chi,
          sku_name_sc,
          sku_status
        `)
        .eq('company_id', companyFilter);

      const productsData = await fetchAllData<any>(productsQuery);

      // 處理每個產品並進行核對
      const verifiedProducts: ProductVerification[] = productsData.map(verifyProduct);

      setProducts(verifiedProducts);
      setFilteredProducts(verifiedProducts);

      toast.success(
        language === 'zh' 
          ? `已載入 ${verifiedProducts.length} 個產品` 
          : `Loaded ${verifiedProducts.length} products`
      );
    } catch (error: any) {
      console.error('加載產品數據失敗:', error);
      toast.error(language === 'zh' ? '載入數據失敗' : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // 應用篩選
  const applyFilters = useCallback(() => {
    let filtered = [...products];

    // SKU ID 搜索 (company_sku_key 或 sku_id)
    if (searchSkuId.trim()) {
      const searchTerm = searchSkuId.toLowerCase().trim();
      filtered = filtered.filter(product =>
        product.sku_id.toLowerCase().includes(searchTerm) ||
        product.company_sku_key.toLowerCase().includes(searchTerm)
      );
    }

    // 條碼搜索
    if (searchBarcode.trim()) {
      const searchTerm = searchBarcode.trim();
      filtered = filtered.filter(product =>
        product.barcode && product.barcode.includes(searchTerm)
      );
    }

    // 產品名稱搜索（中文或英文）
    if (searchProductName.trim()) {
      const searchTerm = searchProductName.toLowerCase().trim();
      filtered = filtered.filter(product => {
        const name = language === 'zh' ? product.sku_name_chi : product.sku_name;
        return name.toLowerCase().includes(searchTerm);
      });
    }

    // 品牌搜索
    if (searchBrand.trim()) {
      const searchTerm = searchBrand.toLowerCase().trim();
      filtered = filtered.filter(product =>
        product.brand.toLowerCase().includes(searchTerm)
      );
    }

    // 狀態篩選
    if (filterStatus !== 'all') {
      filtered = filtered.filter(product => product.sku_status === filterStatus.toUpperCase());
    }

    // 核對結果篩選
    if (filterVerification !== 'all') {
      filtered = filtered.filter(product => product.verification_status === filterVerification);
    }

    setFilteredProducts(filtered);
  }, [products, searchSkuId, searchBarcode, searchProductName, searchBrand, filterStatus, filterVerification, language]);

  // 監聽篩選條件變化
  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  // 初始化 - 獲取當前公司
  useEffect(() => {
    const initialize = async () => {
      try {
        const company = await companyService.getCurrentCompany();
        if (company) {
          setCurrentCompany({ id: String(company.id), code: company.code, name: company.name });
        }
      } catch (error) {
        console.error('初始化失敗:', error);
        toast.error(language === 'zh' ? '初始化失敗' : 'Initialization failed');
      }
    };

    initialize();
  }, [language]);

  // 首次載入時檢查是否有待處理的搜尋篩選條件
  useEffect(() => {
    const pending = pickReloadSearch();
    if (!pending || pending.filters?.source !== 'expiry_search') return;

    console.log('🔄 檢測到待處理的到期日核對搜尋，開始恢復篩選條件並執行搜尋');

    // 恢復篩選條件到UI狀態
    if (pending.filters.searchSkuId !== undefined) setSearchSkuId(pending.filters.searchSkuId);
    if (pending.filters.searchBarcode !== undefined) setSearchBarcode(pending.filters.searchBarcode);
    if (pending.filters.searchProductName !== undefined) setSearchProductName(pending.filters.searchProductName);
    if (pending.filters.searchBrand !== undefined) setSearchBrand(pending.filters.searchBrand);
    if (pending.filters.filterStatus !== undefined) setFilterStatus(pending.filters.filterStatus);
    if (pending.filters.filterVerification !== undefined) setFilterVerification(pending.filters.filterVerification);

    // 執行搜尋
    const executePendingSearch = async () => {
      try {
        await ensureAuthReady();

        // 重新獲取公司資訊
        const company = await companyService.getCurrentCompany();
        if (!company) {
          toast.error(language === 'zh' ? '請先選擇公司' : 'Please select a company first');
          return;
        }

        // 執行搜尋
        await loadProducts(company.id);

        toast.success(language === 'zh' ? '搜尋條件已恢復並執行' : 'Search conditions restored and executed');
      } catch (error) {
        console.error('回放到期日核對搜尋失敗:', error);
        toast.error(language === 'zh' ? '搜尋恢復失敗，請重新搜尋' : 'Search restore failed, please search again');
      }
    };

    executePendingSearch();
  }, [language]); // 移除其他依賴避免無限循環

  // 開始搜索
  const handleStartSearch = () => {
    if (!currentCompany) return;

    // 🔄 使用專業的重新連接搜尋機制
    console.log('🔄 觸發專業重新連接搜尋機制，保存篩選條件並重新載入頁面');

    // 收集所有篩選條件
    const searchFilters = {
      searchSkuId,
      searchBarcode,
      searchProductName,
      searchBrand,
      filterStatus,
      filterVerification
    };

    triggerReloadWithExpirySearchFilters(searchFilters);
  };

  // 切換展開/收起詳細資訊
  const toggleRowExpansion = (skuId: string) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(skuId)) {
        newSet.delete(skuId);
      } else {
        newSet.add(skuId);
      }
      return newSet;
    });
  };

  // 匯出 Excel
  const handleExportExcel = () => {
    if (filteredProducts.length === 0) {
      toast.error(language === 'zh' ? '沒有資料可匯出' : 'No data to export');
      return;
    }

    const exportData = filteredProducts.map(product => ({
      'SKU ID': product.sku_id,
      [language === 'zh' ? '品牌' : 'Brand']: product.brand,
      [language === 'zh' ? '產品名稱' : 'Product Name']: getProductDisplayName(product),
      [language === 'zh' ? '狀態' : 'Status']: product.sku_status,
      [language === 'zh' ? '核對結果' : 'Verification']: 
        product.verification_status === 'MATCHED' ? (language === 'zh' ? '日期相同' : 'All Matched') :
        product.verification_status === 'MISMATCHED' ? (language === 'zh' ? '不相符' : 'Mismatched') :
        (language === 'zh' ? '未有日期' : 'No Data'),
      [language === 'zh' ? 'sku_name 日期' : 'sku_name Date']: formatDate(product.date_from_name),
      [language === 'zh' ? 'sku_name_chi 日期' : 'sku_name_chi Date']: formatDate(product.date_from_chi),
      [language === 'zh' ? 'sku_name_sc 日期' : 'sku_name_sc Date']: formatDate(product.date_from_sc),
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    
    // 設置 SKU 列為文字格式（保留前導零）
    const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1');
    for (let row = range.s.r + 1; row <= range.e.r; row++) {
      const cellAddress = XLSX.utils.encode_cell({ r: row, c: 0 });
      if (worksheet[cellAddress]) {
        worksheet[cellAddress].t = 's'; // 設為文字類型
      }
    }

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, language === 'zh' ? '到期日核對' : 'Expiry Verification');

    const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
    const filename = `expiry_verification_${currentCompany?.code || 'export'}_${timestamp}.xlsx`;
    
    XLSX.writeFile(workbook, filename);
    toast.success(language === 'zh' ? '匯出成功' : 'Export successful');
  };

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              onClick={() => {
                localStorage.setItem('selectedCategory', EXPIRY_CATEGORY);
                navigate(ROUTES.dashboard.root);
              }}
              className="bg-card/50 border-border text-muted-foreground hover:bg-accent/50"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              {language === 'zh' ? '返回到期日總表' : 'Back to Expiry Summary'}
            </Button>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-6 h-6 text-green-400" />
              <h1 className="text-2xl font-bold bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">
                {language === 'zh' ? '到期日核對' : 'Expiry Date Verification'}
              </h1>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {/* 頁面導覽按鈕 */}
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/product-overview/expiry')}
                className="bg-card/50 border-border text-muted-foreground hover:bg-accent/50"
                title={language === 'zh' ? '到期日總表' : 'Expiry Summary'}
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                <Calendar className="w-4 h-4" />
              </Button>
              <div className="flex items-center gap-2 px-3 py-1.5 bg-green-600/20 border border-green-500/30 rounded-md">
                <CheckCircle2 className="w-4 h-4 text-green-400" />
                <span className="text-green-300 text-sm font-medium">
                  {language === 'zh' ? '到期日核對' : 'Expiry Verification'}
                </span>
              </div>
              <Button
                variant="outline"
                size="sm"
                disabled
                className="bg-card/30 border-border text-muted-foreground cursor-not-allowed opacity-50"
                title={language === 'zh' ? '沒有下一頁' : 'No next page'}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>

            {/* 匯出按鈕 */}
            <Button
              onClick={handleExportExcel}
              disabled={filteredProducts.length === 0}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              <Download className="w-4 h-4 mr-2" />
              {language === 'zh' ? '匯出 Excel' : 'Export Excel'}
            </Button>
          </div>
        </div>

        {/* Filters */}
        <Card className="bg-card/80 border-border mb-6">
          <CardHeader>
            <CardTitle className="text-foreground">
              {language === 'zh' ? '篩選條件' : 'Filters'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-4">
              {/* SKU ID 搜索 */}
              <div>
                <label className="text-foreground text-sm mb-2 block">
                  {language === 'zh' ? 'SKU ID' : 'SKU ID'}
                </label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder={language === 'zh' ? '輸入 SKU ID' : 'Enter SKU ID'}
                    value={searchSkuId}
                    onChange={(e) => setSearchSkuId(e.target.value)}
                    className="pl-10 bg-background/50 border-input text-foreground placeholder-muted-foreground"
                  />
                </div>
              </div>

              {/* 條碼搜索 */}
              <div>
                <label className="text-foreground text-sm mb-2 block">
                  {language === 'zh' ? '條碼' : 'Barcode'}
                </label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder={language === 'zh' ? '輸入條碼' : 'Enter Barcode'}
                    value={searchBarcode}
                    onChange={(e) => setSearchBarcode(e.target.value)}
                    className="pl-10 bg-background/50 border-input text-foreground placeholder-muted-foreground"
                  />
                </div>
              </div>

              {/* 產品名稱搜索 */}
              <div>
                <label className="text-foreground text-sm mb-2 block">
                  {language === 'zh' ? '產品名稱' : 'Product Name'}
                </label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder={language === 'zh' ? '輸入產品名稱' : 'Enter Product Name'}
                    value={searchProductName}
                    onChange={(e) => setSearchProductName(e.target.value)}
                    className="pl-10 bg-background/50 border-input text-foreground placeholder-muted-foreground"
                  />
                </div>
              </div>

              {/* 品牌搜索 */}
              <div>
                <label className="text-foreground text-sm mb-2 block">
                  {language === 'zh' ? '品牌' : 'Brand'}
                </label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder={language === 'zh' ? '輸入品牌名稱' : 'Enter Brand Name'}
                    value={searchBrand}
                    onChange={(e) => setSearchBrand(e.target.value)}
                    className="pl-10 bg-background/50 border-input text-foreground placeholder-muted-foreground"
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* 狀態篩選 */}
              <div>
                <label className="text-foreground text-sm mb-2 block">
                  {language === 'zh' ? '狀態' : 'Status'}
                </label>
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger className="bg-background/50 border-input text-foreground">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-popover border-border">
                    <SelectItem value="all" className="text-foreground">{language === 'zh' ? '全部' : 'All'}</SelectItem>
                    <SelectItem value="online" className="text-foreground">ONLINE</SelectItem>
                    <SelectItem value="offline" className="text-foreground">OFFLINE</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* 核對結果篩選 */}
              <div>
                <label className="text-foreground text-sm mb-2 block">
                  {language === 'zh' ? '核對結果' : 'Verification Result'}
                </label>
                <Select value={filterVerification} onValueChange={setFilterVerification}>
                  <SelectTrigger className="bg-background/50 border-input text-foreground">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-popover border-border">
                    <SelectItem value="all" className="text-foreground">{language === 'zh' ? '顯示所有' : 'Show All'}</SelectItem>
                    <SelectItem value="MATCHED" className="text-foreground">{language === 'zh' ? '日期相同' : 'All Matched'}</SelectItem>
                    <SelectItem value="MISMATCHED" className="text-foreground">{language === 'zh' ? '不相符' : 'Mismatched'}</SelectItem>
                    <SelectItem value="NO_DATA" className="text-foreground">{language === 'zh' ? '未有日期' : 'No Data'}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* 搜索按鈕 */}
              <div className="flex items-end">
                <Button
                  onClick={handleStartSearch}
                  disabled={loading}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                >
                  {loading ? (language === 'zh' ? '搜尋中...' : 'Searching...') : (language === 'zh' ? '開始搜尋' : 'Start Search')}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Data Table */}
        <Card className="bg-card/80 border-border">
          <CardHeader>
            <CardTitle className="text-foreground flex items-center justify-between">
              <span>{language === 'zh' ? '核對結果列表' : 'Verification Results'}</span>
              <span className="text-sm font-normal text-muted-foreground">
                {language === 'zh' ? `共 ${filteredProducts.length} 個產品` : `${filteredProducts.length} products`}
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-400 mx-auto"></div>
                <p className="text-muted-foreground mt-2">{language === 'zh' ? '載入中...' : 'Loading...'}</p>
              </div>
            ) : filteredProducts.length === 0 ? (
              <div className="text-center py-12">
                <MinusCircle className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">
                  {language === 'zh' ? '沒有找到產品。請調整篩選條件或點擊「開始搜尋」' : 'No products found. Please adjust filters or click "Start Search"'}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-border">
                      <TableHead className="text-muted-foreground w-10"></TableHead>
                      <TableHead className="text-muted-foreground">SKU ID</TableHead>
                      <TableHead className="text-muted-foreground">{language === 'zh' ? '品牌' : 'Brand'}</TableHead>
                      <TableHead className="text-muted-foreground">{language === 'zh' ? '產品名稱' : 'Product Name'}</TableHead>
                      <TableHead className="text-muted-foreground">{language === 'zh' ? '狀態' : 'Status'}</TableHead>
                      <TableHead className="text-muted-foreground">{language === 'zh' ? '核對結果' : 'Verification'}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredProducts.map((product) => {
                      const isExpanded = expandedRows.has(product.sku_id);
                      return (
                        <React.Fragment key={product.sku_id}>
                          <TableRow className="border-border hover:bg-accent/30 cursor-pointer">
                            <TableCell onClick={() => toggleRowExpansion(product.sku_id)}>
                              {isExpanded ? (
                                <ChevronUp className="w-4 h-4 text-muted-foreground" />
                              ) : (
                                <ChevronDown className="w-4 h-4 text-muted-foreground" />
                              )}
                            </TableCell>
                            <TableCell className="text-foreground font-mono">{product.sku_id}</TableCell>
                            <TableCell className="text-foreground">{product.brand}</TableCell>
                            <TableCell className="text-foreground">{getProductDisplayName(product)}</TableCell>
                            <TableCell>{getStatusBadge(product.sku_status)}</TableCell>
                            <TableCell>{getVerificationBadge(product.verification_status)}</TableCell>
                          </TableRow>
                          {isExpanded && (
                            <TableRow className="border-border bg-accent/20">
                              <TableCell colSpan={6} className="p-4">
                                <div className="space-y-2 text-sm">
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                      <p className="text-muted-foreground mb-1">sku_name:</p>
                                      <p className="text-foreground mb-1 break-all">
                                        {product.sku_name
                                          ? decodeHtmlEntities(product.sku_name)
                                          : (language === 'zh' ? '（無）' : '(None)')}
                                      </p>
                                      <p className="text-blue-400 font-mono">
                                        {language === 'zh' ? '日期：' : 'Date: '}
                                        {formatDate(product.date_from_name)}
                                      </p>
                                    </div>
                                    <div>
                                      <p className="text-muted-foreground mb-1">sku_name_chi:</p>
                                      <p className="text-foreground mb-1 break-all">
                                        {product.sku_name_chi
                                          ? decodeHtmlEntities(product.sku_name_chi)
                                          : (language === 'zh' ? '（無）' : '(None)')}
                                      </p>
                                      <p className="text-blue-400 font-mono">
                                        {language === 'zh' ? '日期：' : 'Date: '}
                                        {formatDate(product.date_from_chi)}
                                      </p>
                                    </div>
                                    <div>
                                      <p className="text-muted-foreground mb-1">sku_name_sc:</p>
                                      <p className="text-foreground mb-1 break-all">
                                        {product.sku_name_sc
                                          ? decodeHtmlEntities(product.sku_name_sc)
                                          : (language === 'zh' ? '（無）' : '(None)')}
                                      </p>
                                      <p className="text-blue-400 font-mono">
                                        {language === 'zh' ? '日期：' : 'Date: '}
                                        {formatDate(product.date_from_sc)}
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ProductExpiryProcessing;

